import React, { useState } from "react";
import styled from "styled-components";
import { Classes, Tag, Menu, MenuItem } from "@blueprintjs/core";
import { Popover2 } from "@blueprintjs/popover2";
import {
  Box,
  Text,
  quarks,
  Stack,
  PageSection,
  CenterSpinner,
  ErrorState,
} from "ui-components";
import { TaskT } from "ui-io/task-io/task-t";
import copy from "copy-to-clipboard";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";

import moment from "moment";
import toasts from "../../utils/toasts";
import { formatLocation } from "../../routes";
import {
  ApproveSubmissionButton,
  RejectSubmissionButton,
  RejectToEdit,
  StartEditTaskButton,
} from "../../components/task-actions";
import { goToTask } from "./go-to-task";
import TaskExtraActions from "./task-extra-actions";
import AuthorizedAgencyIcon from "../../components/authorized-agency-icon";
import FlagTask from "../../components/task-actions/flag-task";
import { gotToView } from "../tasks/go-to-inbox";
import { InboxViewT } from "../tasks/inbox-views";
import PublishSubmissionButton from "../../components/task-actions/publish-submission-button";
import useSession from "ui-io/use-session";
import RefreshButton from "../../components/refresh-button";
import { useQueryClient } from "react-query";
import { TASKS_QUERY_FILTER } from "ui-io/queries/tasks/task-query";
type Props = {
  isLoading: boolean;
  isError: boolean;
  task: TaskT;
  view?: InboxViewT;
};

const TaskHeader = ({ isLoading, isError, task, view = "Inbox" }: Props) => {
  const [isModelOpen, setModelOpen] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();
  if (isLoading) {
    return (
      <Box mt="medium" mb="medium" p="xxsmall">
        <CenterSpinner />
      </Box>
    );
  } else if (isError) {
    return (
      <Box mt="medium" mb="medium" p="xxsmall">
        <ErrorState />
      </Box>
    );
  }

  const { session } = useSession();
  const userRole = session?.role || "Admin";

  const items = getTaskDetailsItems(task, view);

  const handleClickToCopy = () => {
    copy(task.id);
    toasts.success("Copied task ID to clipboard!");
  };

  const history = useNavigate();
  const goBackToCurrentView = () => {
    if (!!view) {
      gotToView(history, { view: view });
    } else {
      // goToAdmin(history)
    }
  };

  const handleRefreshTask = () => {
    try {
      setIsRefreshing(true);

      queryClient.refetchQueries([TASKS_QUERY_FILTER]);
    } finally {
      setTimeout(() => setIsRefreshing(false), 1000);
    }
  };

  const Breadcrumbs = (
    <Box flexDir="row">
      <Text
        small
        muted
        interactive
        onClick={() => goBackToCurrentView()}
        inline
      >
        Go back to view
      </Text>
      <Box ml="small" mr="small">
        <Text small muted>
          /
        </Text>
      </Box>
      <Text small muted interactive onClick={handleClickToCopy} inline>
        {task.id}
      </Text>
    </Box>
  );

  const PrepareMenuItems = ({ itemValue }: { itemValue: ItemValueT }) => {
    return !itemValue.isLink ? (
      <Text interactive small>
        {itemValue.description}
      </Text>
    ) : (
      <MenuItem
        onClick={() =>
          itemValue.taskId
            ? goToTask(history, {
                taskId: itemValue.taskId,
                tabId: "Inbox",
                subtabId: "company",
              })
            : undefined
        }
        text={`${itemValue.description?.substring(0, 20)} ${
          itemValue.description && itemValue.description?.length > 20
            ? "..."
            : ""
        }`}
        intent="primary"
        title={itemValue.description}
      />
    );
  };

  const PrepareRelationshipItems = ({
    itemValue,
  }: {
    itemValue: ItemValueT;
  }) => (
    <MenuItem
      text={
        <>
          <Box>
            {`${itemValue.type?.substring(0, 20)} ${
              itemValue.type && itemValue.type?.length > 20 ? "..." : ""
            }`}
          </Box>
          <Box>{itemValue.target}</Box>
        </>
      }
      intent="primary"
      title={itemValue.type}
    />
  );

  const Actions = !task.isCompleted ? (
    <Stack horizontal spacing={quarks.margins.small}>
      <RefreshButton
        className="mr-sm"
        isLoading={isRefreshing}
        handleSubmit={() => handleRefreshTask()}
      />
      <FlagTask task={task} />
      <StartEditTaskButton task={task} />
      <ApproveSubmissionButton task={task} />
      {userRole == "SUPER_ADMIN" && view == "InReview" ? (
        <>
          <PublishSubmissionButton task={task} />
          <RejectToEdit
            task={task}
            isModalOpen={isModelOpen}
            setModelOpen={setModelOpen}
          />
          <RejectSubmissionButton task={task} />
        </>
      ) : null}

      {/* <TaskTestRunButton
        task={task}
        onTestRunClick={() => setWaitingForStart(true)}
      /> */}
      <TaskExtraActions task={task} prevInboxView={view} />
    </Stack>
  ) : null;

  return (
    <Box mb="medium" p="xxsmall">
      <PageSection
        title={task.companyName || ""}
        actions={Actions}
        breadcrumbs={Breadcrumbs}
        isFastlane={task.isFastlaneTask}
        small
        authorizedAgency={
          task._raw?.configuration?.is_authorized ? (
            <AuthorizedAgencyIcon marginLeft={2} />
          ) : null
        }
      >
        <DescriptionList>
          {items.map(
            ({ id, value, label, externalLink, internalLink, values }) => {
              return (
                <Box key={id}>
                  <Text caption muted mb="xsmall">
                    {label}
                  </Text>
                  {(() => {
                    if (externalLink) {
                      return (
                        <a
                          href={externalLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {value}
                        </a>
                      );
                    }
                    if (internalLink) {
                      return <Link to={internalLink}>{value}</Link>;
                    }
                    if (id === "stateChanged") {
                      const date = value && new Date(value);
                      if (Number(value) < moment().subtract(1, "w").valueOf()) {
                        return (
                          <Tag intent="danger" minimal>
                            {date ? moment(date).fromNow() : "N/A"}
                          </Tag>
                        );
                      } else {
                        return (
                          <Text>{date ? moment(date).fromNow() : "N/A"}</Text>
                        );
                      }
                    }

                    if (id === "isConflated") {
                      return (
                        <Tag
                          intent={`${value === "true" ? "primary" : "danger"}`}
                          minimal
                        >
                          {value}
                        </Tag>
                      );
                    }

                    if (values && values?.length > 0) {
                      return (
                        <Box>
                          <Popover2
                            interactionKind="hover"
                            content={
                              <Menu>
                                {values.map((itemValue, index) => (
                                  <>
                                    <PrepareMenuItems
                                      itemValue={itemValue}
                                      key={index}
                                    />
                                    <PrepareRelationshipItems
                                      itemValue={itemValue}
                                      key={index}
                                    />
                                  </>
                                ))}
                              </Menu>
                            }
                          >
                            <span className={Classes.TOOLTIP_INDICATOR}>
                              {`${label} ...`}
                            </span>
                          </Popover2>
                        </Box>
                      );
                    }

                    return <Text>{value}</Text>;
                  })()}
                </Box>
              );
            },
          )}
        </DescriptionList>
      </PageSection>
    </Box>
  );
};

type ItemValueT = {
  taskId?: string;
  tag?: string;
  description?: string;
  isLink?: boolean;
  target?: number;
  type?: string;
};

type TaskDetailsItem = {
  id: string;
  value: string | number;
  label: string;
  externalLink?: string;
  internalLink?: string;
  values?: ItemValueT[];
};

const getTaskDetailsItems = (
  task: TaskT,
  view: InboxViewT,
  newBrandTaskId?: string,
  // maintenanceTasks?: AllTasksByConfigIdResultT,
  // apolloData?: ApolloDetailsT,
) => {
  // const { lastTestRun } = task
  // const runId = lastTestRun?.run_id
  // const jobUrl = runId ? formatLocation.testScrape({ jobRunId: runId }) : ''

  const gotoNewBrandTask = newBrandTaskId
    ? formatLocation.task({ taskId: newBrandTaskId, tabId: view })
    : "";

  // const taskItemValues = (tasks?: AllTasksByConfigIdResultT) =>
  //   tasks?.map((task) => ({
  //     taskId: task.task_id,
  //     description: task.description,
  //     isLink: true,
  //   }))
  // const brandRelationships = (apolloData?: ApolloDetailsT) =>
  //   apolloData?.relationship &&
  //   apolloData?.relationship?.map((item) => ({
  //     target: item.target,
  //     type: item.type,
  //   }))
  const items: TaskDetailsItem[] = [
    // {
    //   id: 'testRun',
    //   value: runId ? runId : 'N/A',
    //   label: 'Test Run',
    //   internalLink: jobUrl,
    // },
    {
      id: "status",
      value: task.currentStateReadable ?? "N/A",
      label: "Status",
    },
    {
      id: "stateChanged",
      value: task.inCurrentStateTime ?? "N/A",
      label: "Last update",
    },
    // {
    //   id: 'projectId',
    //   value: task.info.project ?? 'N/A',
    //   label: 'Project',
    // },
    {
      id: "new-brand-task",
      value: newBrandTaskId ? "New Brand task" : "N/A",
      label: "New Brand task",
      internalLink: gotoNewBrandTask,
    },
    // {
    //   id: 'maintenance-task',
    //   value: 'N/A',
    //   label: 'Maintenance tasks',
    //   values: taskItemValues(maintenanceTasks),
    // },
    {
      id: "assignee",
      value: task.assignee ?? "N/A",
      label: "Assignee",
    },
    {
      id: "priority",
      value: task.taskPriority ?? "N/A",
      label: "priority",
    },
    // {
    //   id: 'description',
    //   value: task?.description ?? 'N/A',
    //   label: 'Description',
    // },
    {
      id: "websiteUrl",
      value: "URL",
      label: "Website",
      externalLink: task._raw.companyDetail?.url?.website
        ? task._raw.companyDetail?.url?.website
        : undefined,
    },

    {
      id: "strategy",
      value: task.configScrapeStrategy
        ? task.configScrapeStrategy
        : task.type
          ? task.type
          : "N/A",
      label: "Strategy",
    },
    // {
    //   id: 'isConflated',
    //   value: apolloData?.isConflation.toString() ?? 'N/A',
    //   label: 'Is Conflated',
    // },
    // {
    //   id: 'relationships',
    //   value: 'N/A',
    //   label: 'Relationships',
    //   values: apolloData ? brandRelationships(apolloData) : [],
    // },
    // {
    //   id: 'details',
    //   value: task.details ?? 'N/A',
    //   label: 'Details',
    // },
  ];

  return items;
};

const DescriptionList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${quarks.margins.large};
  margin: 15px 0px 10px 0px;
`;

export default TaskHeader;
